import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fab,
  Tooltip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Label as LabelIcon,
  Category as CategoryIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getImageLabels,
  addImageLabel,
  updateImageLabel,
  deleteImageLabel,
  getImageObjects,
  addImageObject,
  updateImageObject,
  deleteImageObject
} from '../services/api';

const MotionChip = motion(Chip);
const MotionCard = motion(Card);

const LabelObjectEditor = ({ imageId, onUpdate }) => {
  const [labels, setLabels] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showObjects, setShowObjects] = useState(true);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'label' or 'object'
  const [dialogMode, setDialogMode] = useState(''); // 'add' or 'edit'
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    confidence: 0.8,
    category: '',
    x_min: 0,
    y_min: 0,
    x_max: 1,
    y_max: 1
  });

  // Notification states
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  }, [imageId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [labelsResponse, objectsResponse] = await Promise.all([
        getImageLabels(imageId),
        getImageObjects(imageId)
      ]);
      setLabels(labelsResponse.labels || []);
      setObjects(objectsResponse.objects || []);
    } catch (error) {
      console.error('Error loading labels/objects:', error);
      showNotification('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const openDialog = (type, mode, item = null) => {
    setDialogType(type);
    setDialogMode(mode);
    setEditingItem(item);

    if (mode === 'edit' && item) {
      setFormData({
        name: item.name || '',
        confidence: item.confidence || 0.8,
        category: item.category || '',
        x_min: item.x_min || 0,
        y_min: item.y_min || 0,
        x_max: item.x_max || 1,
        y_max: item.y_max || 1
      });
    } else {
      setFormData({
        name: '',
        confidence: 0.8,
        category: '',
        x_min: 0,
        y_min: 0,
        x_max: 1,
        y_max: 1
      });
    }

    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({
      name: '',
      confidence: 0.8,
      category: '',
      x_min: 0,
      y_min: 0,
      x_max: 1,
      y_max: 1
    });
  };

  const handleSave = async () => {
    try {
      if (dialogType === 'label') {
        const labelData = {
          name: formData.name,
          confidence: formData.confidence,
          category: formData.category || null,
          source: 'user'
        };

        if (dialogMode === 'add') {
          await addImageLabel(imageId, labelData);
          showNotification('Label added successfully');
        } else {
          await updateImageLabel(imageId, editingItem.id, labelData);
          showNotification('Label updated successfully');
        }
      } else {
        const objectData = {
          name: formData.name,
          confidence: formData.confidence,
          x_min: formData.x_min,
          y_min: formData.y_min,
          x_max: formData.x_max,
          y_max: formData.y_max
        };

        if (dialogMode === 'add') {
          await addImageObject(imageId, objectData);
          showNotification('Object added successfully');
        } else {
          await updateImageObject(imageId, editingItem.id, objectData);
          showNotification('Object updated successfully');
        }
      }

      await loadData();
      handleCloseDialog();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving:', error);
      showNotification('Failed to save changes', 'error');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      if (type === 'label') {
        await deleteImageLabel(imageId, id);
        showNotification('Label deleted successfully');
      } else {
        await deleteImageObject(imageId, id);
        showNotification('Object deleted successfully');
      }

      await loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      showNotification('Failed to delete item', 'error');
    }
  };

  const renderLabels = () => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <LabelIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />
        <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          Labels ({labels.length})
        </Typography>
        {editMode && (
          <Tooltip title="Add Label">
            <IconButton
              size="small"
              onClick={() => openDialog('label', 'add')}
              sx={{ color: 'rgba(102, 126, 234, 0.8)' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <AnimatePresence>
          {labels.map((label) => (
            <MotionChip
              key={label.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{label.name}</span>
                  {label.confidence && (
                    <span style={{ opacity: 0.7 }}>
                      ({Math.round(label.confidence * 100)}%)
                    </span>
                  )}
                  {label.source === 'user' && (
                    <span style={{ opacity: 0.5, fontSize: '0.7em' }}>👤</span>
                  )}
                </Box>
              }
              size="small"
              sx={{
                backgroundColor: label.source === 'user'
                  ? 'rgba(102, 126, 234, 0.2)'
                  : 'rgba(76, 175, 80, 0.2)',
                color: 'rgba(255, 255, 255, 0.9)',
                border: `1px solid ${label.source === 'user'
                  ? 'rgba(102, 126, 234, 0.3)'
                  : 'rgba(76, 175, 80, 0.3)'}`,
                '&:hover': {
                  backgroundColor: label.source === 'user'
                    ? 'rgba(102, 126, 234, 0.3)'
                    : 'rgba(76, 175, 80, 0.3)',
                }
              }}
              onDelete={editMode ? () => handleDelete('label', label.id) : undefined}
              onClick={editMode ? () => openDialog('label', 'edit', label) : undefined}
              deleteIcon={
                editMode ? (
                  <DeleteIcon sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />
                ) : undefined
              }
            />
          ))}
        </AnimatePresence>
      </Box>
    </Box>
  );

  const renderObjects = () => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CategoryIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />
        <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          Objects ({objects.length})
        </Typography>
        {editMode && (
          <Tooltip title="Add Object">
            <IconButton
              size="small"
              onClick={() => openDialog('object', 'add')}
              sx={{ color: 'rgba(156, 39, 176, 0.8)' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={1}>
        <AnimatePresence>
          {objects.map((object) => (
            <Grid item xs={12} sm={6} key={object.id}>
              <MotionCard
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                variant="outlined"
                sx={{
                  background: 'rgba(156, 39, 176, 0.1)',
                  border: '1px solid rgba(156, 39, 176, 0.3)',
                  cursor: editMode ? 'pointer' : 'default'
                }}
                onClick={editMode ? () => openDialog('object', 'edit', object) : undefined}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                        {object.name}
                      </Typography>
                      {object.confidence && (
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                          Confidence: {Math.round(object.confidence * 100)}%
                        </Typography>
                      )}
                      {(object.x_min !== null && object.y_min !== null) && (
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                          Position: ({object.x_min?.toFixed(2)}, {object.y_min?.toFixed(2)}) - ({object.x_max?.toFixed(2)}, {object.y_max?.toFixed(2)})
                        </Typography>
                      )}
                    </Box>
                    {editMode && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete('object', object.id);
                        }}
                        sx={{ color: 'rgba(244, 67, 54, 0.8)' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </AnimatePresence>
      </Grid>
    </Box>
  );

  const renderDialog = () => (
    <Dialog
      open={dialogOpen}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(26, 26, 46, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{ color: 'white' }}>
        {dialogMode === 'add' ? 'Add' : 'Edit'} {dialogType === 'label' ? 'Label' : 'Object'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(102, 126, 234, 0.8)' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' }
            }}
          />

          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
              Confidence: {Math.round(formData.confidence * 100)}%
            </Typography>
            <Slider
              value={formData.confidence}
              onChange={(e, value) => setFormData({ ...formData, confidence: value })}
              min={0}
              max={1}
              step={0.01}
              sx={{
                color: 'rgba(102, 126, 234, 0.8)',
                '& .MuiSlider-thumb': {
                  backgroundColor: 'rgba(102, 126, 234, 0.8)',
                },
                '& .MuiSlider-track': {
                  backgroundColor: 'rgba(102, 126, 234, 0.8)',
                },
              }}
            />
          </Box>

          {dialogType === 'label' && (
            <TextField
              label="Category (Optional)"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(102, 126, 234, 0.8)' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' }
              }}
            />
          )}

          {dialogType === 'object' && (
            <>
              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Bounding Box (normalized coordinates 0-1)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="X Min"
                    type="number"
                    value={formData.x_min}
                    onChange={(e) => setFormData({ ...formData, x_min: parseFloat(e.target.value) })}
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                        '&.Mui-focused fieldset': { borderColor: 'rgba(102, 126, 234, 0.8)' }
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Y Min"
                    type="number"
                    value={formData.y_min}
                    onChange={(e) => setFormData({ ...formData, y_min: parseFloat(e.target.value) })}
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                        '&.Mui-focused fieldset': { borderColor: 'rgba(102, 126, 234, 0.8)' }
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="X Max"
                    type="number"
                    value={formData.x_max}
                    onChange={(e) => setFormData({ ...formData, x_max: parseFloat(e.target.value) })}
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                        '&.Mui-focused fieldset': { borderColor: 'rgba(102, 126, 234, 0.8)' }
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Y Max"
                    type="number"
                    value={formData.y_max}
                    onChange={(e) => setFormData({ ...formData, y_max: parseFloat(e.target.value) })}
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                        '&.Mui-focused fieldset': { borderColor: 'rgba(102, 126, 234, 0.8)' }
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' }
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!formData.name.trim()}
          sx={{
            background: 'linear-gradient(45deg, #667eea, #764ba2)',
            '&:hover': {
              background: 'linear-gradient(45deg, #5a67d8, #6b46c1)',
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Edit Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'white' }}>
          AI Analysis
        </Typography>
        <Button
          startIcon={editMode ? <SaveIcon /> : <EditIcon />}
          onClick={() => setEditMode(!editMode)}
          sx={{
            color: editMode ? 'rgba(76, 175, 80, 0.8)' : 'rgba(255, 255, 255, 0.6)',
            '&:hover': {
              backgroundColor: editMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          {editMode ? 'Done' : 'Edit'}
        </Button>
      </Box>

      {/* Labels Section */}
      {renderLabels()}

      {/* Objects Section */}
      {renderObjects()}

      {/* Dialog */}
      {renderDialog()}

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            background: notification.severity === 'error'
              ? 'rgba(244, 67, 54, 0.9)'
              : 'rgba(76, 175, 80, 0.9)',
            color: 'white'
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LabelObjectEditor;